import { ParentFilterGroup } from 'src/context/reducers/filterSetReducer';
import { FilterType } from 'src/context/reducers/filterSetTypes';
import React, { ReactNode } from 'react';
import {
  SITE_OF_CARE_DISPLAY_TYPE,
  collectSiteOfCareLabels,
  isSocFilterType,
  isSocSuppressedFilterType,
} from 'src/components/FilterSets/SiteOfCare/displayHelpers';
import type { SiteOfCareType } from 'src/api/redux/suggestions/suggestionsApi';

const getDisplayTypeForFilterType = (type: string): string => {
  if (isSocFilterType(type)) {
    return SITE_OF_CARE_DISPLAY_TYPE;
  }
  if (type === FilterType.SITE_SPECIALTY) {
    return 'Site Specialty';
  }
  let displayType =
    type.charAt(0).toUpperCase() +
    type.slice(1).toLowerCase().replace(/_/g, ' ');
  if (displayType === 'Payer type') {
    displayType = 'Payer';
  }
  return displayType;
};

export const renderFilterDescription = (
  filter: ParentFilterGroup,
  renderFilterElements: (
    filtersByType: Record<string, string[]>,
    hasExcludeFilter: boolean
  ) => ReactNode,
  socOptions: SiteOfCareType[]
): ReactNode => {
  // Check if any filter has is_exclude: true
  let hasExcludeFilter = false;

  if (filter?.filters?.length > 0) {
    hasExcludeFilter = filter.filters.some((f) => f.is_exclude === true);
  }

  if (!hasExcludeFilter && filter?.detailed_filters?.length > 0) {
    hasExcludeFilter = filter.detailed_filters.some(
      (f) => f.is_exclude === true
    );
  }
  // First check for detailed_filters
  if (
    filter &&
    filter.detailed_filters &&
    Array.isArray(filter.detailed_filters) &&
    filter.detailed_filters.length > 0
  ) {
    // Group filters by type
    const filtersByType: Record<string, string[]> = {};

    const siteOfCareLabels = collectSiteOfCareLabels(
      filter.detailed_filters,
      socOptions
    );
    if (siteOfCareLabels.length > 0) {
      filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
    }

    // Process each detailed filter
    filter.detailed_filters.forEach((filterItem: any) => {
      const type = filterItem.type;
      if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
      const displayType = getDisplayTypeForFilterType(type);

      // Skip if there are no detailed values
      if (
        !filterItem.detailed_values ||
        !Array.isArray(filterItem.detailed_values) ||
        filterItem.detailed_values.length === 0
      ) {
        return;
      }

      // Initialize the array for this type if it doesn't exist
      if (!filtersByType[displayType]) {
        filtersByType[displayType] = [];
      }

      // Add all labels from detailed_values to the array for this type
      filterItem.detailed_values.forEach((detailedValue: any) => {
        if (
          detailedValue?.label &&
          !filtersByType[displayType].includes(detailedValue?.label)
        ) {
          filtersByType[displayType].push(detailedValue?.label);
        }
      });
    });

    // If we found detailed filters, return them
    if (Object.keys(filtersByType).length > 0) {
      return renderFilterElements(filtersByType, hasExcludeFilter);
    }
  }

  // Fallback to regular filters if detailed_filters is not available
  if (
    filter &&
    filter.filters &&
    Array.isArray(filter.filters) &&
    filter.filters.length > 0
  ) {
    // Group filters by type
    const filtersByType: Record<string, string[]> = {};

    const siteOfCareLabels = collectSiteOfCareLabels(filter.filters, socOptions);
    if (siteOfCareLabels.length > 0) {
      filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
    }

    // Process each filter
    filter.filters.forEach((filterItem: any) => {
      const type = filterItem.type;
      if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
      const displayType = getDisplayTypeForFilterType(type);

      // Skip if there are no values
      if (
        !filterItem?.values ||
        !Array.isArray(filterItem?.values) ||
        filterItem?.values?.length === 0
      ) {
        return;
      }

      // Initialize the array for this type if it doesn't exist
      if (!filtersByType[displayType]) {
        filtersByType[displayType] = [];
      }

      // Add all values to the array for this type
      filterItem?.values?.forEach((value: any) => {
        const valueLabel =
          typeof value === 'string' ? value : value?.label || value?.toString();
        if (!filtersByType[displayType].includes(valueLabel)) {
          filtersByType[displayType].push(valueLabel);
        }
      });
    });

    // If we found filters, return them
    if (Object.keys(filtersByType).length > 0) {
      return renderFilterElements(filtersByType, hasExcludeFilter);
    }
  }

  // If no filter types were found, check if we can extract info from the label
  const filtersByType: Record<string, string[]> = {};
  if (filter?.label?.includes('Oncologist')) {
    filtersByType['Specialty'] = ['Oncology'];
  }
  if (filter?.label?.includes('Johns Hopkins')) {
    filtersByType['Med. School'] = ['Johns Hopkins'];
  }

  // Add company-wide info if applicable
  if (filter?.scope && filter?.scope.toLowerCase() === 'company') {
    filtersByType['Company'] = ['Company-wide filter'];
  }

  return renderFilterElements(filtersByType, hasExcludeFilter);
};

// Helper function to render filter elements from grouped filters
export const renderFilterElements = (
  filtersByType: Record<string, string[]>,
  hasExcludeFilter = false
): ReactNode => {
  // Convert the grouped filters to JSX
  const filterElements: React.JSX.Element[] = [];

  // Set prefix based on whether any filter has is_exclude: true
  const prefixText = hasExcludeFilter ? 'Excludes:' : 'Includes:';

  // Add prefix at the beginning
  filterElements.push(
    <span
      key="includes-prefix"
      className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
    >
      {prefixText}{' '}
    </span>
  );

  Object.entries(filtersByType).forEach(([type, values], index) => {
    // Add separator if not the first item
    if (index > 0) {
      filterElements.push(
        <span
          key={`separator-${index}`}
          className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
        >
          ;{' '}
        </span>
      );
    }

    // Add type label
    filterElements.push(
      <span
        key={`type-${index}`}
        className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
      >
        {type}:{' '}
      </span>
    );

    // Add values
    values.forEach((value, valueIndex) => {
      // Add comma if not the first value
      if (valueIndex > 0) {
        filterElements.push(
          <span
            key={`comma-${index}-${valueIndex}`}
            className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
          >
            ,{' '}
          </span>
        );
      }

      // Add value
      filterElements.push(
        <span
          key={`value-${index}-${valueIndex}`}
          className="text-tertiary text-xs font-bold font-['Inter'] leading-none"
        >
          {value}
        </span>
      );
    });
  });

  // If no filter elements were created (beyond the "Includes:" prefix), return a default message
  if (filterElements.length <= 1) {
    return (
      <span className="text-tertiary text-xs font-normal font-['Inter'] leading-none">
        No filter details available
      </span>
    );
  }

  return filterElements;
};
